import { Trash2 } from "lucide-react";
import type { RecurringException } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { minutesToTime, timeToMinutes } from "@/features/calendar/time-helpers";
import { OccurrencePreview } from "./OccurrencePreview";
import { RecurrenceFreqFields } from "./RecurrenceFreqFields";

interface Props {
  rule: RecurringException;
  disabled?: boolean;
  onChange: (rule: RecurringException) => void;
  onRemove: () => void;
}

/** Troca a FORMA preservando o que as duas têm em comum (kind, janela, motivo). */
function changeFreq(
  rule: RecurringException,
  freq: RecurringException["freq"]
): RecurringException {
  const common = {
    kind: rule.kind,
    ...(rule.startMinute === undefined ? {} : { startMinute: rule.startMinute }),
    ...(rule.endMinute === undefined ? {} : { endMinute: rule.endMinute }),
    ...(rule.reason ? { reason: rule.reason } : {}),
  };
  if (freq === "YEARLY") return { freq, month: 12, day: 25, ...common };
  if (freq === "WEEKLY") return { freq, weekday: 0, ...common };
  return { freq, weekday: 1, ordinal: 1, ...common };
}

// Uma regra recorrente em edição, com a prévia do servidor logo abaixo.
export function RecurrenceRuleRow({ rule, disabled, onChange, onRemove }: Props) {
  const isExtra = rule.kind === "EXTRA_WINDOW";

  const setKind = (kind: RecurringException["kind"]) => {
    if (kind === "CLOSED") {
      // Os extremos SAEM do objeto, não viram `undefined`: um `startMinute: undefined` sobrevive ao
      // `JSON.stringify` como chave ausente, mas atravessa o schema Zod como chave PRESENTE e
      // indefinida — e a união discriminada trata os dois casos igual só por sorte.
      const next: Record<string, unknown> = { ...rule, kind: "CLOSED" };
      delete next.startMinute;
      delete next.endMinute;
      onChange(next as RecurringException);
      return;
    }
    // Janela extra precisa dos dois extremos — sem eles a regra é descartada em silêncio pelos
    // dois resolvedores (falha fechada). Semear 08:00–12:00 dá ao gestor algo para ajustar.
    onChange({ ...rule, kind: "EXTRA_WINDOW", startMinute: 480, endMinute: 720 });
  };

  return (
    <div className="space-y-2 rounded-md border border-border-subtle px-2 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select
          aria-label="Repetição"
          className="h-9 w-[110px]"
          value={rule.freq}
          disabled={disabled}
          onChange={(e) => onChange(changeFreq(rule, e.target.value as RecurringException["freq"]))}
        >
          <option value="WEEKLY">Semanal</option>
          <option value="MONTHLY">Mensal</option>
          <option value="YEARLY">Anual</option>
        </Select>

        <RecurrenceFreqFields rule={rule} disabled={disabled} onChange={onChange} />

        <Select
          aria-label="Efeito"
          className="h-9 w-[130px]"
          value={rule.kind}
          disabled={disabled}
          onChange={(e) => setKind(e.target.value as RecurringException["kind"])}
        >
          <option value="CLOSED">Fecha o dia</option>
          <option value="EXTRA_WINDOW">Janela extra</option>
        </Select>

        {isExtra ? (
          <>
            <Input
              aria-label="Início da janela"
              type="time"
              className="h-9 w-[104px]"
              value={minutesToTime(rule.startMinute ?? 480)}
              disabled={disabled}
              onChange={(e) => onChange({ ...rule, startMinute: timeToMinutes(e.target.value) })}
            />
            <span className="text-text-tertiary">–</span>
            <Input
              aria-label="Fim da janela"
              type="time"
              className="h-9 w-[104px]"
              value={minutesToTime(rule.endMinute ?? 720)}
              disabled={disabled}
              onChange={(e) => {
                // Mesma convenção do editor de turnos: fim < início ⇒ atravessa a meia-noite.
                const start = rule.startMinute ?? 480;
                const end = timeToMinutes(e.target.value);
                onChange({ ...rule, endMinute: end > start ? end : end + 1440 });
              }}
            />
          </>
        ) : null}

        <Input
          aria-label="Motivo"
          placeholder="Motivo (opcional)"
          className="h-9 min-w-[120px] flex-1"
          value={rule.reason ?? ""}
          disabled={disabled}
          onChange={(e) => onChange({ ...rule, reason: e.target.value })}
        />

        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remover regra"
          disabled={disabled}
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4 text-rd-danger-500" aria-hidden />
        </Button>
      </div>

      <OccurrencePreview rule={rule} />
    </div>
  );
}
